
import React, { useEffect } from 'react';
import { X, Handshake, BarChart3, FileCheck, TrendingUp, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

export const SponsorshipModal = ({ isOpen, onClose }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const features = [
    {
      icon: BarChart3,
      title: 'Demographic-Based Event Selection',
      description: 'Strategic event selection through comprehensive demographic analysis ensures maximum alignment with your target audience and brand objectives.',
      details: [
        'Target audience analysis and profiling',
        'Event demographic research and mapping',
        'Audience overlap assessment',
        'Strategic event recommendation matrix'
      ]
    },
    {
      icon: TrendingUp,
      title: 'ROI Measurement & Analysis',
      description: 'Comprehensive tracking and analysis of sponsorship investments with detailed reporting on brand awareness, engagement, and conversion metrics.',
      details: [
        'Pre and post-event brand awareness studies',
        'Engagement tracking across all touchpoints',
        'Lead generation and conversion analysis',
        'Comprehensive ROI reporting dashboard'
      ]
    },
    {
      icon: FileCheck,
      title: 'Contract Negotiation Expertise',
      description: 'Expert negotiation services ensure you get maximum value from every sponsorship deal while protecting your brand interests and investment.',
      details: [
        'Deal structure optimization',
        'Terms and conditions negotiation',
        'Risk mitigation strategies',
        'Performance milestone setting'
      ]
    },
    {
      icon: Handshake,
      title: 'Comprehensive Sales Strategies',
      description: 'Leverage sponsorship opportunities as powerful sales tools to drive customer acquisition, market penetration, and revenue growth.',
      details: [
        'Lead generation through sponsorship activations',
        'Customer acquisition cost optimization',
        'Sales funnel integration strategies',
        'Revenue impact measurement'
      ]
    }
  ];

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-1 sm:p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg sm:rounded-3xl max-w-5xl max-h-[98vh] sm:max-h-[90vh] w-full overflow-hidden shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Fixed */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-2 sm:p-6 text-white relative flex-shrink-0">
          <button
            onClick={onClose}
            className="absolute top-1 right-1 sm:top-4 sm:right-4 p-1 sm:p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-4 h-4 sm:w-6 sm:h-6" />
          </button>
          <div className="flex items-center gap-1 sm:gap-4 pr-6 sm:pr-12">
            <Handshake className="w-4 h-4 sm:w-10 sm:h-10 text-white flex-shrink-0" style={{strokeWidth: '1.5', fill: 'none'}} />
            <div className="min-w-0">
              <h2 className="text-sm sm:text-3xl font-bold leading-tight">Sponsorship Management</h2>
              <p className="text-white/90 text-xs sm:text-lg mt-0 sm:mt-1 hidden sm:block">Strategic Partnerships That Drive Results</p>
            </div>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-2 sm:p-6">
          <div className="mb-3 sm:mb-6">
            <p className="text-gray-700 text-sm sm:text-lg leading-relaxed">
              We find sponsorship deals that work for your brand and your budget. Smart partnerships with real audiences that care about what you're selling.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-6">
            {features.map((feature, index) => (
              <div key={index} className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl sm:rounded-2xl p-3 sm:p-6 border border-purple-100">
                <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-4">
                  <div className="w-8 h-8 sm:w-12 sm:h-12 flex items-center justify-center">
                    <feature.icon className="w-4 h-4 sm:w-6 sm:h-6 text-purple-600" style={{strokeWidth: '1.5', fill: 'none'}} />
                  </div>
                  <h3 className="text-sm sm:text-xl font-bold text-gray-900">{feature.title}</h3>
                </div>

                <p className="text-gray-700 mb-2 sm:mb-4 leading-relaxed text-xs sm:text-base">{feature.description}</p>

                <ul className="space-y-1 sm:space-y-2">
                  {feature.details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                      <ArrowRight className="w-2 h-2 sm:w-3 sm:h-3 text-purple-600 flex-shrink-0" style={{strokeWidth: '1.5', fill: 'none'}} />
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Contact CTA */}
          <div className="mt-4 sm:mt-8 p-3 sm:p-6 bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl sm:rounded-2xl text-white text-center">
            <h4 className="text-sm sm:text-xl font-bold mb-1 sm:mb-2">Ready to Maximize Your Sponsorship ROI?</h4>
            <p className="text-gray-300 mb-2 sm:mb-4 text-xs sm:text-base">Let's create partnerships that deliver measurable results</p>
            <Button 
              onClick={onClose}
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:opacity-90 transition-opacity px-4 sm:px-8 text-xs sm:text-base"
            >
              Get Started
              <ArrowRight className="ml-1 sm:ml-2 w-3 h-3 sm:w-4 sm:h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
